// Renderer for scheme background image (blueprint/plan)

import * as THREE from 'three'
import type { GardenScene } from './GardenScene'
import { debug } from '@/utils/debug'
import type { SchemeImage, SchemeOutline } from '@/types'

// Conversion constants (50 dots per meter)
const DPM_SCALE = 1.0 / 50

export class SchemeRenderer {
  private scene: GardenScene
  private mesh: THREE.Mesh | null = null
  private outlineMesh: THREE.Mesh | null = null
  private textureLoader: THREE.TextureLoader
  private visible: boolean = true

  constructor(scene: GardenScene) {
    this.scene = scene
    this.textureLoader = new THREE.TextureLoader()
  }

  /**
   * Load and render the scheme background image
   */
  async load(schemeImage: SchemeImage): Promise<void> {
    // Check if we have an image URL
    if (!schemeImage.imageUrl) {
      debug.log('Scheme', 'No scheme image URL provided, skipping')
      return
    }

    debug.log('Scheme', `Loading scheme image: ${schemeImage.imageUrl}`)
    debug.log('Scheme', `Position: (${schemeImage.x}, ${schemeImage.y}), Scale: ${schemeImage.scale}`)

    try {
      // Load the texture
      const texture = await this.loadTexture(schemeImage.imageUrl)

      // Get image dimensions from texture
      const imageWidth = texture.image.width
      const imageHeight = texture.image.height
      debug.log('Scheme', `Image dimensions: ${imageWidth}x${imageHeight}px`)

      // Apply scale factor (fallback to 1 if invalid)
      const scale = schemeImage.scale > 0 ? schemeImage.scale : 1

      // Calculate display size in points, then convert to world units
      // The scale is applied to the image size
      const displayWidthPoints = imageWidth * scale
      const displayHeightPoints = imageHeight * scale

      const displayWidth = displayWidthPoints * DPM_SCALE
      const displayHeight = displayHeightPoints * DPM_SCALE

      debug.log('Scheme', `Display size: ${displayWidthPoints}x${displayHeightPoints} points (${displayWidth.toFixed(2)}x${displayHeight.toFixed(2)}m)`)

      // Create geometry
      const geometry = new THREE.PlaneGeometry(displayWidth, displayHeight)

      // Create material with the texture
      // Use MeshBasicMaterial since this is a background image (no lighting needed)
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        depthWrite: false, // Prevent z-fighting with ground shapes
        side: THREE.DoubleSide
      })

      // Remove existing mesh if any
      this.dispose()

      // Create mesh
      this.mesh = new THREE.Mesh(geometry, material)

      // Position the mesh
      // x, y are top-left corner in points
      // Three.js plane is centered, so we need to offset by half the display size
      const posX = (schemeImage.x + displayWidthPoints / 2) * DPM_SCALE
      const posZ = (schemeImage.y + displayHeightPoints / 2) * DPM_SCALE

      // Place slightly below ground level to ensure it's behind ground shapes
      const posY = -0.02

      this.mesh.position.set(posX, posY, posZ)

      // Rotate to be horizontal (XZ plane)
      this.mesh.rotation.x = -Math.PI / 2

      // Set render order to ensure it renders before ground shapes
      this.mesh.renderOrder = -1

      // Apply visibility
      this.mesh.visible = this.visible

      // Add to scene
      this.scene.getScene().add(this.mesh)

      debug.success('Scheme', `Scheme image rendered at (${posX.toFixed(2)}, ${posY}, ${posZ.toFixed(2)})`)

    } catch (error) {
      debug.error('Scheme', 'Failed to load scheme image', error)
    }
  }

  /**
   * Load texture with promise wrapper
   */
  private loadTexture(url: string): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        url,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace
          resolve(texture)
        },
        undefined,
        (error) => {
          reject(error)
        }
      )
    })
  }

  /**
   * Render the garden boundary outline as a 3D tube
   */
  renderOutline(outline: SchemeOutline): void {
    // Guard: need at least 3 points for a valid polygon
    if (!outline.points || outline.points.length < 3) {
      debug.log('Scheme', 'Skipping outline: insufficient points')
      return
    }

    // Clean up existing outline mesh if any
    if (this.outlineMesh) {
      this.scene.getScene().remove(this.outlineMesh)
      this.outlineMesh.geometry.dispose()
      ;(this.outlineMesh.material as THREE.Material).dispose()
      this.outlineMesh = null
    }

    // Convert 2D points to 3D path
    const points3D = outline.points.map(p =>
      new THREE.Vector3(
        (outline.x + p.x) * DPM_SCALE,
        0.025,  // Slightly above ground
        (outline.y + p.y) * DPM_SCALE
      )
    )

    // Close the loop by connecting back to the first point
    points3D.push(points3D[0].clone())

    // Create path and tube geometry
    const path = new THREE.CatmullRomCurve3(points3D, false, 'catmullrom', 0)
    const geometry = new THREE.TubeGeometry(path, points3D.length * 10, 0.025, 8, false)
    const material = new THREE.MeshStandardMaterial({ color: 0xff0000 })

    this.outlineMesh = new THREE.Mesh(geometry, material)
    this.outlineMesh.castShadow = true
    this.outlineMesh.receiveShadow = true
    this.outlineMesh.visible = this.visible
    this.scene.getScene().add(this.outlineMesh)

    debug.success('Scheme', `Outline rendered with ${outline.points.length} points`)
  }

  /**
   * Set visibility of the scheme layer
   */
  setVisible(visible: boolean): void {
    this.visible = visible
    if (this.mesh) {
      this.mesh.visible = visible
    }
    if (this.outlineMesh) {
      this.outlineMesh.visible = visible
    }
    debug.log('Scheme', `Visibility set to: ${visible}`)
  }

  /**
   * Get current visibility state
   */
  isVisible(): boolean {
    return this.visible
  }

  /**
   * Check if scheme is loaded
   */
  isLoaded(): boolean {
    return this.mesh !== null
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    if (this.mesh) {
      this.scene.getScene().remove(this.mesh)
      this.mesh.geometry.dispose()
      if (this.mesh.material instanceof THREE.Material) {
        const material = this.mesh.material as THREE.MeshBasicMaterial
        material.map?.dispose()
        material.dispose()
      }
      this.mesh = null
    }
    if (this.outlineMesh) {
      this.scene.getScene().remove(this.outlineMesh)
      this.outlineMesh.geometry.dispose()
      ;(this.outlineMesh.material as THREE.Material).dispose()
      this.outlineMesh = null
    }
    debug.log('Scheme', 'Scheme renderer disposed')
  }
}
